import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, FileText } from "lucide-react";

const EMPTY = {
  name: "",
  bio: "",
  cv_text: "",
  cv_url: "",
  cv_filename: "",
  portrait_url: "",
  website: "",
  instagram: "",
  past_shows_at_carpe_diem: [],
};

export default function AdminArtists() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(null); // "portrait" | "cv" | null
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/artists");
      setItems(r.data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...EMPTY, ...a }); setEditingId(a.id); setOpen(true); };

  const uploadFile = async (file, kind, targetKey, filenameKey) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    setUploading(kind);
    try {
      const { data } = await api.post("/gallery/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, [targetKey]: data.url, ...(filenameKey ? { [filenameKey]: data.filename } : {}) }));
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally { setUploading(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/artists/${editingId}`, form);
        toast.success("Artist updated");
      } else {
        await api.post("/admin/artists", form);
        toast.success("Artist added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete artist "${a.name}"? Their bio will be removed but artworks will stay.`)) return;
    try {
      await api.delete(`/admin/artists/${a.id}`);
      toast.success("Artist deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-artists-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="overline">Roster</div>
          <h1 className="display text-4xl mt-2">Artists</h1>
          <p className="text-muted2 text-sm mt-2">{items.length} artist{items.length !== 1 ? "s" : ""} on file. Bios show up on the Buy Art page.</p>
        </div>
        <button onClick={openCreate} className="btn-primary" data-testid="artist-add-btn">
          <Plus size={14} /> Add artist
        </button>
      </div>

      {loading ? (
        <div className="text-muted2">Loading…</div>
      ) : (
        <div className="grid gap-3">
          {items.map(a => (
            <div key={a.id} className="border border-soft bg-white p-4 flex gap-4 items-start" data-testid={`artist-row-${a.id.slice(0,8)}`}>
              <div className="w-16 h-16 bg-cream-2 border border-soft overflow-hidden shrink-0 flex items-center justify-center">
                {a.portrait_url ? (
                  <img src={a.portrait_url} alt={a.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="font-serif text-2xl text-muted2">{a.name.charAt(0)}</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg">{a.name}</div>
                <div className="text-xs text-muted2 mt-0.5">
                  Bio: {a.bio ? `${a.bio.length} chars` : "—"} · CV: {a.cv_text ? `${a.cv_text.length} chars` : a.cv_url ? a.cv_filename || "file" : "—"}
                </div>
                {a.bio && <p className="text-sm mt-2 text-muted2 line-clamp-2">{a.bio}</p>}
              </div>
              <button onClick={() => openEdit(a)} className="p-2 hover:bg-cream-2" data-testid={`artist-edit-${a.id.slice(0,8)}`}><Pencil size={14} /></button>
              <button onClick={() => del(a)} className="p-2 hover:bg-cream-2 text-red-700" data-testid={`artist-delete-${a.id.slice(0,8)}`}><Trash2 size={14} /></button>
            </div>
          ))}
          {items.length === 0 && <div className="text-muted2">No artists yet.</div>}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 md:p-10 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-cream max-w-3xl w-full border border-soft" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-soft">
              <div className="font-serif text-2xl">{editingId ? "Edit artist" : "Add artist"}</div>
              <button onClick={() => setOpen(false)} data-testid="artist-modal-close"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-5" data-testid="artist-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Portrait */}
                <div>
                  <div className="overline mb-2">Portrait</div>
                  {form.portrait_url ? (
                    <div className="relative w-32 h-32 border border-soft bg-cream-2">
                      <img src={form.portrait_url} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setForm(f => ({ ...f, portrait_url: "" }))} className="absolute -top-2 -right-2 bg-charcoal text-cream p-1 rounded-full"><X size={12} /></button>
                    </div>
                  ) : (
                    <label className="inline-flex items-center gap-2 border border-dashed border-soft px-3 py-3 cursor-pointer hover:bg-cream-2 text-xs w-32 justify-center">
                      <Upload size={12} /> {uploading === "artist-portrait" ? "…" : "Upload"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "artist-portrait", "portrait_url")} data-testid="artist-portrait-input" />
                    </label>
                  )}
                </div>

                {/* Fields */}
                <div className="md:col-span-2 space-y-4">
                  <label className="block">
                    <span className="overline">Full name *</span>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" data-testid="artist-name" />
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                      <span className="overline">Website</span>
                      <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" placeholder="https://…" data-testid="artist-website" />
                    </label>
                    <label className="block">
                      <span className="overline">Instagram</span>
                      <input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" placeholder="@handle" data-testid="artist-instagram" />
                    </label>
                  </div>
                </div>
              </div>

              <label className="block">
                <span className="overline">Short bio (shown on Buy Art page)</span>
                <textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full bg-cream-2/50 border border-soft p-3 text-sm focus:outline-none focus:border-charcoal mt-1" placeholder="A 2–4 sentence intro about the artist and their practice." data-testid="artist-bio" />
              </label>

              <label className="block">
                <span className="overline">Full CV / write-up (paste text)</span>
                <textarea rows={10} value={form.cv_text} onChange={(e) => setForm({ ...form, cv_text: e.target.value })} className="w-full bg-cream-2/50 border border-soft p-3 text-sm font-mono focus:outline-none focus:border-charcoal mt-1" placeholder="Education · Solo shows · Group shows · Awards · Press…" data-testid="artist-cv-text" />
              </label>

              <div>
                <div className="overline mb-2">Or attach CV PDF</div>
                {form.cv_url ? (
                  <div className="flex items-center gap-3 border border-soft bg-white px-3 py-2 max-w-sm">
                    <FileText size={16} />
                    <a href={form.cv_url} target="_blank" rel="noreferrer" className="text-sm truncate flex-1 link-hover">{form.cv_filename || "View CV"}</a>
                    <button type="button" onClick={() => setForm(f => ({ ...f, cv_url: "", cv_filename: "" }))}><X size={14} /></button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 border border-dashed border-soft px-4 py-3 cursor-pointer hover:bg-cream-2 text-sm">
                    <Upload size={14} /> {uploading === "artist-cv" ? "Uploading…" : "Upload CV (PDF)"}
                    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "artist-cv", "cv_url", "cv_filename")} data-testid="artist-cv-input" />
                  </label>
                )}
              </div>

              <div>
                <div className="overline mb-2">Past exhibitions at Carpe Diem</div>
                <div className="space-y-2">
                  {(form.past_shows_at_carpe_diem || []).map((show, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`past-show-${i}`}>
                      <input
                        type="text"
                        value={show.year || ""}
                        onChange={(e) => {
                          const arr = [...form.past_shows_at_carpe_diem];
                          arr[i] = { ...arr[i], year: e.target.value };
                          setForm({ ...form, past_shows_at_carpe_diem: arr });
                        }}
                        placeholder="Year"
                        className="w-20 bg-transparent border-b border-soft py-1.5 text-sm focus:outline-none focus:border-charcoal"
                      />
                      <input
                        type="text"
                        value={show.title || ""}
                        onChange={(e) => {
                          const arr = [...form.past_shows_at_carpe_diem];
                          arr[i] = { ...arr[i], title: e.target.value };
                          setForm({ ...form, past_shows_at_carpe_diem: arr });
                        }}
                        placeholder="Show title (e.g. 'Rural Harmony')"
                        className="flex-1 bg-transparent border-b border-soft py-1.5 text-sm focus:outline-none focus:border-charcoal"
                      />
                      <input
                        type="text"
                        value={show.notes || ""}
                        onChange={(e) => {
                          const arr = [...form.past_shows_at_carpe_diem];
                          arr[i] = { ...arr[i], notes: e.target.value };
                          setForm({ ...form, past_shows_at_carpe_diem: arr });
                        }}
                        placeholder="Notes (optional)"
                        className="flex-1 bg-transparent border-b border-soft py-1.5 text-sm focus:outline-none focus:border-charcoal"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const arr = form.past_shows_at_carpe_diem.filter((_, j) => j !== i);
                          setForm({ ...form, past_shows_at_carpe_diem: arr });
                        }}
                        className="p-1.5 hover:bg-cream-2 text-red-700"
                        data-testid={`past-show-remove-${i}`}
                      ><X size={12} /></button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, past_shows_at_carpe_diem: [...(form.past_shows_at_carpe_diem || []), { year: "", title: "", notes: "" }] })}
                    className="text-xs uppercase tracking-widest px-3 py-2 border border-soft hover:bg-cream-2"
                    data-testid="past-show-add"
                  >+ Add show</button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-soft">
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost" data-testid="artist-cancel">Cancel</button>
                <button type="submit" className="btn-primary" disabled={uploading} data-testid="artist-submit">
                  {editingId ? "Save changes" : "Add artist"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
